import {User} from 'firebase/auth';
import {create} from 'zustand';
import {
  checkIfSignInWithEmailLink,
  completeSignInWithEmailLink,
  getCurrentUser,
  getUserProfile,
  loginUser,
  logoutUser,
  onAuthChange,
  registerUser,
  sendSignInLink,
  updateOnboardingSteps,
  updateUserProfile,
  updateUserSubscription,
  UserProfile,
  UserSubscription,
} from '../utils/firebase';
import {logger} from '../utils/logger';

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  authListener: (() => void) | null;
  emailLinkSent: boolean;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  sendLoginLink: (email: string) => Promise<void>;
  completeLoginWithEmailLink: (email: string) => Promise<void>;
  checkEmailLink: () => boolean;
  register: (email: string, password: string, name?: string, subscription?: Partial<UserSubscription>) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (userId: string, profile: Partial<UserProfile>) => Promise<void>;
  updateSubscription: (userId: string, subscription: Partial<UserSubscription>) => Promise<void>;
  updateOnboardingProgress: (userId: string, stepId: string, completed: boolean) => Promise<void>;
}

export const useAuthStore = create<AuthState>(set => ({
  user: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  authListener: null,
  emailLinkSent: false,

  initialize: async () => {
    try {
      set({isLoading: true, error: null});

      // Set up auth state change listener first
      const unsubscribe = onAuthChange(async user => {
        logger.info(`Auth state changed: ${user?.uid || 'signed out'}`);
        if (user?.uid) {
          const profile = await getUserProfile(user.uid);
          logger.info(`User profile loaded: ${profile ? 'success' : 'not found'}`);
          set({user, profile, isAuthenticated: true, isLoading: false});
        } else {
          set({user: null, profile: null, isAuthenticated: false, isLoading: false});
        }
      });

      // Store the unsubscribe function
      set({authListener: unsubscribe});

      // Check if user is already authenticated
      const user = await getCurrentUser();
      logger.info(`Auth store initialized with user: ${user?.uid || 'not signed in'}`);

      if (user?.uid) {
        const profile = await getUserProfile(user.uid);
        logger.info(`User profile loaded: ${profile ? 'success' : 'not found'}`);
        set({user, profile, isAuthenticated: true, isLoading: false});
      } else {
        set({user: null, profile: null, isAuthenticated: false, isLoading: false});
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error initializing auth';
      logger.error('Error initializing auth', errorMessage);
      set({isLoading: false, error: errorMessage});
    }
  },

  login: async (email, password) => {
    try {
      set({isLoading: true, error: null});
      logger.info(`Attempting login for: ${email}`);

      const user = await loginUser(email, password);
      if (user?.uid) {
        logger.info('Login successful, loading profile');
        const profile = await getUserProfile(user.uid);
        set({user, profile, isAuthenticated: true, isLoading: false});
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown login error';
      logger.error('Login error', errorMessage);
      set({isLoading: false, error: errorMessage});
    }
  },

  register: async (email, password, name, subscription) => {
    try {
      set({isLoading: true, error: null});
      logger.info('Starting user registration', {subscription});

      // Ensure plan is required and defaults to free
      const subscriptionDetails: UserSubscription = {
        plan: (subscription?.plan || 'free') as 'free' | 'pro',
        status: (subscription?.status || 'active') as 'active' | 'cancelled' | 'past_due',
        ...(subscription?.billing ? {billing: subscription.billing} : {}),
        ...(subscription?.stripeCustomerId ? {stripeCustomerId: subscription.stripeCustomerId} : {}),
        ...(subscription?.stripeSubscriptionId ? {stripeSubscriptionId: subscription.stripeSubscriptionId} : {}),
        ...(subscription?.currentPeriodEnd ? {currentPeriodEnd: subscription.currentPeriodEnd} : {}),
      };

      const profile: Partial<UserProfile> = {
        email,
        name,
        subscription: subscriptionDetails,
      };

      logger.info('Registering user with Firebase', {email, name, subscriptionType: subscriptionDetails.plan});
      const result = await registerUser(email, password, profile);

      if (result.user?.uid) {
        logger.info(`Registration successful: ${result.user.uid}`);
        // Get the fresh profile from Firestore
        const freshProfile = await getUserProfile(result.user.uid);
        logger.info('Fresh profile loaded', freshProfile);

        set({
          user: result.user,
          profile: freshProfile,
          isAuthenticated: true,
          isLoading: false,
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown registration error';
      logger.error('Registration error', errorMessage);
      set({isLoading: false, error: errorMessage});
    }
  },

  logout: async () => {
    try {
      set({isLoading: true, error: null});
      logger.info('Logging out user');

      // Get the current unsubscribe function
      const {authListener} = useAuthStore.getState();

      // Unsubscribe from auth state changes to prevent race conditions
      if (authListener) {
        authListener();
      }

      // Call Firebase signOut
      await logoutUser();

      // Clear any local storage data that might be persisting auth state
      localStorage.removeItem('firebase:authUser');
      localStorage.removeItem('firebase:session');

      logger.info('Logout successful');

      // Reset auth state
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
        isLoading: false,
        authListener: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown logout error';
      logger.error('Logout error:', errorMessage);
      set({isLoading: false, error: errorMessage});

      // Force reset auth state even on error
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
      });
    }
  },

  updateProfile: async (userId, profile) => {
    try {
      set({isLoading: true, error: null});
      logger.info('Updating user profile', {userId, profile});

      await updateUserProfile(userId, profile);

      // Get the updated profile
      const updatedProfile = await getUserProfile(userId);
      logger.info('Profile updated, new profile', updatedProfile);

      set({profile: updatedProfile, isLoading: false});
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown profile update error';
      logger.error('Update profile error', errorMessage);
      set({isLoading: false, error: errorMessage});
    }
  },

  updateSubscription: async (userId, subscription) => {
    try {
      set({isLoading: true, error: null});
      logger.info('Updating subscription for user', {userId, subscription});

      await updateUserSubscription(userId, subscription);

      // Get the updated profile
      const updatedProfile = await getUserProfile(userId);
      logger.info('Subscription updated, new profile', updatedProfile?.subscription);

      set({profile: updatedProfile, isLoading: false});
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown subscription update error';
      logger.error('Update subscription error', errorMessage);
      set({isLoading: false, error: errorMessage});
    }
  },

  // New method for sending login link
  sendLoginLink: async email => {
    try {
      set({isLoading: true, error: null, emailLinkSent: false});
      logger.info(`Sending login link to: ${email}`);

      await sendSignInLink(email);

      set({isLoading: false, emailLinkSent: true});
      logger.info('Login link sent successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error sending login link';
      logger.error('Error sending login link', errorMessage);
      set({isLoading: false, error: errorMessage, emailLinkSent: false});
    }
  },

  // Check if current URL is a sign-in link
  checkEmailLink: () => {
    return checkIfSignInWithEmailLink();
  },

  // Complete the sign-in with email link process
  completeLoginWithEmailLink: async email => {
    try {
      set({isLoading: true, error: null});
      logger.info(`Completing login with email link for: ${email}`);

      const user = await completeSignInWithEmailLink(email);

      if (user?.uid) {
        logger.info('Email link login successful, loading profile');
        const profile = await getUserProfile(user.uid);
        set({user, profile, isAuthenticated: true, isLoading: false});
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown email link login error';
      logger.error('Email link login error', errorMessage);
      set({isLoading: false, error: errorMessage});
    }
  },

  updateOnboardingProgress: async (userId: string, stepId: string, completed: boolean) => {
    try {
      // Don't set isLoading to true for onboarding updates to prevent UI flash
      logger.info('Updating onboarding progress', {userId, stepId, completed});

      await updateOnboardingSteps(userId, stepId, completed);

      // Get the updated profile
      const updatedProfile = await getUserProfile(userId);
      logger.info('Onboarding progress updated, new profile', updatedProfile);

      set({profile: updatedProfile});
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown onboarding update error';
      logger.error('Update onboarding error', errorMessage);
    }
  },
}));
